import React, { Fragment, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TreeView } from "@components_v2/tree-view";
import FolderTreeViewSkeleton from "@components/tree/components/folder-tree-view-skeleton";
import { GROUPING_VIEW_TYPE } from "@constants";
import { Typography } from "@mui/material";
import { changeFold, changeSelectedItem, clearSelectedByList } from "@modules/categoryTreeView";
import {
    ImageAssetOn,
    ImageCategoryGroupOff,
    ImageCategoryGroupOn,
    ImageUncategoryOff,
    ImageUncategoryOn,
} from "@mzc-cloudplex/icons";
import CategoryTreeViewItem from "@components/tree/components/category-tree-view-item";
import useCheckPermission from "@hooks/useCheckPermission";

const CategoryTreeView = ({ resourceType, searchQueryParams, onClickTreeItem, onDropTreeItem }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();

    const project = useSelector(({ project }) => project);
    const categoryTreeView = useSelector(({ categoryTreeView }) => categoryTreeView);

    const onClickFold = (item) => {
        item.isFold = !item.isFold;

        if (item.children && !item.children.length) {
            item.isEmpty = true;
        }

        dispatch(changeFold(item));
    };

    const convertToStatus = (item) => {
        if (!item) return "";
        let result = [];
        if (!item.isEmpty && !item.isFold) {
            result.push("tree-open");
        }
        if (item.id === categoryTreeView.selectedCategory?.selected?.id) {
            result.push("tree-selected");
        }
        if (item.isEmpty) {
            result.push("tree-empty");
        }

        return result.join(" ");
    };

    const makeMultipleDiv = (item) => {
        if (!item?.code) {
            return "";
        }
        const makeArray = item.code.split("_") || [];
        if (makeArray.length < 1) {
            return "";
        }
        return makeArray.map((item, index) => {
            return <span key={index}></span>;
        });
    };

    const renderTreeNodeIcon = (item) => {
        let result = undefined;
        switch (item.type) {
            case "category":
                break;

            case "categoryGroup":
                result = <ImageCategoryGroupOn width={16} height={16} />;
                break;

            case "uncategorized":
                result = <ImageUncategoryOn width={16} height={16} />;
                break;
        }

        if (
            (!item.isGroup && !(item.id === categoryTreeView.selectedCategory?.selected?.id)) ||
            (item.isGroup && !item.isActive)
        ) {
            switch (item.type) {
                case "category":
                    result = "";
                    break;

                case "categoryGroup":
                    result = <ImageCategoryGroupOff width={16} height={16} />;
                    break;

                case "uncategorized":
                    result = <ImageUncategoryOff width={16} height={16} />;
                    break;
            }
        }
        return result;
    };

    const isClickableCategoryGroup = (item) => {
        if (!item.isRoot || item.isNotCategoryIds) return "";

        if (resourceType === "Asset") return "";
        else if (resourceType === "Video") return "";
        else if (resourceType === "People" || resourceType === "Music" || resourceType === "Photo") return "";
        else return "tree-item-readonly";
    };

    const onHandleClickTreeItem = useCallback(
        (item) => {
            dispatch(changeSelectedItem(item));
            onClickTreeItem({
                ...item,
                hierarchyType: GROUPING_VIEW_TYPE.CATEGORY,
            });
        },
        [onClickTreeItem],
    );

    const selectedCategoryId = categoryTreeView.selectedCategory?.selected?.id;

    const renderTree = (data) => {
        const treeView =
            data &&
            data.length &&
            data.map((item) => {
                if (!item) return;
                const categoryName = item.name === "Uncategorized Assets" ? `Uncategorized ${resourceType}` : item.name;
                return (
                    <Fragment key={item.id}>
                        <CategoryTreeViewItem
                            resourceType={resourceType}
                            data={item}
                            renderTree={renderTree}
                            renderTreeNodeIcon={renderTreeNodeIcon(item)}
                            categoryName={categoryName}
                            onToggleGroupFold={(item) => onClickFold(item)}
                            onClickTreeItem={(item) => onHandleClickTreeItem(item)}
                            onDropTreeItem={onDropTreeItem}
                            selectedCategoryId={selectedCategoryId}
                        />
                    </Fragment>
                );
            });
        return treeView;
    };

    useEffect(() => {
        if (
            !["Asset", "Archived", "Collection"].includes(resourceType) ||
            searchQueryParams.params?.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY
        ) {
            if (categoryTreeView.data && categoryTreeView.selectedByListTargetId) {
                const categoryMap = categoryTreeView.categoryMap;
                const item = categoryMap[categoryTreeView.selectedByListTargetId];
                dispatch(clearSelectedByList());

                if (item) onClickTreeItem(item, true);
            }
        }
    }, [categoryTreeView.selectedByListTargetId]);

    return (
        <>
            <TreeView
                sx={{
                    width: `100%`,
                    minWidth: 318,
                    opacity: 1,
                    overflowY: `auto`,
                    // 트리는 펼치고 접을 때마다 높이가 바뀌어 스크롤바가 생겼다 사라진다.
                    // 자리를 상시 확보해 두지 않으면 노드를 펼칠 때마다 트리 폭이 8px 씩 흔들린다.
                    scrollbarGutter: "stable",
                    transition: `opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 200ms`,
                }}
                expanded={
                    // 설명: 폴더 닫혀있는 상태에서 하위 폴더 생성시 부모 폴더를 열면서 하위폴더를 보여줘야하는데 스토리북에 expansion에 대한 처리가 되어있어 여기서 제어가 불가능. expanded에서 전체를 다 관리
                    categoryTreeView.categoryMap &&
                    Object.values(categoryTreeView.categoryMap)
                        .filter((category) => !category.isFold)
                        .map((category) => category.id)
                }
                selected={[categoryTreeView.selectedCategory?.selected?.id]}
            >
                {categoryTreeView.pending ? (
                    <>
                        <FolderTreeViewSkeleton type={"child"} depth={0} />
                        <CategoryTreeViewItem
                            resourceType={resourceType}
                            data={categoryTreeView.categoryMap[0] ?? categoryTreeView.categoryMap["UCID"]}
                            renderTreeNodeIcon={<ImageAssetOn width={16} height={16} />}
                            foldDisabled={true}
                        />
                    </>
                ) : (
                    <>
                        {categoryTreeView.filter.isFilter ? (
                            categoryTreeView.filter.data && categoryTreeView.filter.data.length ? (
                                <>
                                    {categoryTreeView.filter.data.map((item) => {
                                        return (
                                            <CategoryTreeViewItem
                                                key={`category-treeview-item-${item.id}`}
                                                resourceType={resourceType}
                                                data={item}
                                                renderTree={renderTree}
                                                renderTreeNodeIcon={renderTreeNodeIcon(item)}
                                                onToggleGroupFold={(item) => onClickFold(item)}
                                                onClickTreeItem={(item) => onHandleClickTreeItem(item)}
                                                pending={categoryTreeView.pending}
                                                onDropTreeItem={onDropTreeItem}
                                                selectedCategoryId={selectedCategoryId}
                                            />
                                        );
                                    })}
                                </>
                            ) : (
                                <div className={"empty"}>
                                    <i className="sprite sprite-nohierarchy"></i>
                                    <Typography variant={"body1"}>
                                        {t(`common::label::No {{type}} Categories`, {
                                            type: resourceType,
                                        })}
                                    </Typography>
                                </div>
                            )
                        ) : categoryTreeView?.data?.length > 0 ? (
                            <>
                                {categoryTreeView.data.map((item) => {
                                    return (
                                        <CategoryTreeViewItem
                                            key={`category-treeview-item-${item.id}`}
                                            resourceType={resourceType}
                                            data={item}
                                            renderTree={renderTree}
                                            renderTreeNodeIcon={renderTreeNodeIcon(item)}
                                            onToggleGroupFold={(item) => onClickFold(item)}
                                            onClickTreeItem={(item) => onHandleClickTreeItem(item)}
                                            foldDisabled={item.isRoot || item.isGroup}
                                            pending={categoryTreeView.pending}
                                            onDropTreeItem={onDropTreeItem}
                                            selectedCategoryId={selectedCategoryId}
                                        />
                                    );
                                })}
                            </>
                        ) : (
                            <div className={"empty"}>
                                <i className="sprite sprite-nohierarchy"></i>
                                <Typography variant={"body1"}>
                                    {t(`common::label::No {{type}} Categories`, {
                                        type: resourceType,
                                    })}
                                </Typography>
                            </div>
                        )}
                    </>
                )}
            </TreeView>
        </>
    );
};

export default CategoryTreeView;
